'use client';

import { bunja, createScope } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { PropsWithChildren } from 'react';

import AbstractSocket from '@flipster/socket/AbstractSocket';
import { R230522Scope, nullSocket } from '@flipster/socket/r230522/internal/context';
import { R230522Context, V2SocketEvents } from '@flipster/socket/r230522/socket';

import { r230522NormalSubFlag } from '../flag';
import { authBunja } from '../shared/auth/state';
import { webviewInfoBunja } from '../shared/webview';
import { DatadogR230522Setup } from './datadog';
import { bridgeR230522Socket } from './instance/bridge-socket';
import { createPrivateHybridSocket, createPublicHybridSocket } from './instance/hybrid-socket';
import {
  createPrivateN2FR230522Socket,
  createPrivateR230522Socket,
  createPublicN2FR230522Socket,
  createPublicR230522Socket,
} from './instance/web-socket';

export type SocketType = 'hybrid' | 'bridge' | 'websocket';

export interface R230522ProviderProps extends PropsWithChildren {
  preferredSocketType?: SocketType;
}
export const R230522Provider = ({ children, preferredSocketType = 'hybrid' }: R230522ProviderProps) => {
  const { socketAtom } = useBunja(r230522ProviderBunja, [PreferredSocketTypeScope.bind(preferredSocketType)]);
  const socket = useAtomValue(socketAtom);
  return (
    <R230522Context value={socket}>
      <DatadogR230522Setup />
      {children}
    </R230522Context>
  );
};

const PreferredSocketTypeScope = createScope<string>();

const r230522ProviderBunja = bunja(() => {
  const parentSocket = bunja.use(R230522Scope);
  if (parentSocket !== nullSocket) throw new Error('R230522Context is already wrapped');

  const preferredSocketType = bunja.use(PreferredSocketTypeScope);
  const r230522NormalSub = r230522NormalSubFlag();
  const publicWebSocket = r230522NormalSub ? createPublicN2FR230522Socket() : createPublicR230522Socket();
  const privateWebSocket = r230522NormalSub ? createPrivateN2FR230522Socket() : createPrivateR230522Socket();
  const publicHybridSocket = createPublicHybridSocket();
  const privateHybridSocket = createPrivateHybridSocket();
  const bridgeSocket = bridgeR230522Socket as AbstractSocket<unknown, V2SocketEvents>;

  const { isCookieLoggedInAtom } = bunja.use(authBunja);
  const { webviewAtom } = bunja.use(webviewInfoBunja);

  const hybridSocketAtom = atom((get) => {
    const isCookieLoggedIn = get(isCookieLoggedInAtom);
    if (isCookieLoggedIn) return privateHybridSocket;
    else return publicHybridSocket;
  });

  const webSocketAtom = atom((get) => {
    const isCookieLoggedIn = get(isCookieLoggedInAtom);
    if (isCookieLoggedIn) return privateWebSocket;
    else return publicWebSocket;
  });

  const socketAtom = atom((get) => {
    const isWebview = get(webviewAtom);
    const hybridSocket = get(hybridSocketAtom);
    const webSocket = get(webSocketAtom);
    if (preferredSocketType === 'websocket') return webSocket;
    if (preferredSocketType === 'bridge') return bridgeSocket;
    if (isWebview) return hybridSocket;
    return webSocket;
  });

  return { socketAtom };
});
